import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { apiService } from '../services/api';
import { RecentPost } from '../screens/dashboard/RecentPostsCard';

export interface PostInsightsModalProps {
  visible: boolean;
  pageId: string;
  post: RecentPost | null;
  onClose: () => void;
}

interface PostInsightsData {
  views: number;
  reach: number;
  reactionsTotal: number;
  reactionsByType: Record<string, number>;
  comments: number;
  shares: number;
  linkClicks: number;
  video?: { threeSecondViews: number; views60s: number; watchTimeMinutes: number };
}

const REACTION_TYPES_ORDERED = ['LIKE', 'LOVE', 'CARE', 'HAHA', 'WOW', 'SAD', 'ANGRY'];
const REACTION_ICON: Record<string, { name: keyof typeof Ionicons.glyphMap; color: string }> = {
  LIKE: { name: 'thumbs-up', color: '#1877F2' },
  LOVE: { name: 'heart', color: '#F33E58' },
  CARE: { name: 'heart-circle', color: '#F7B125' },
  HAHA: { name: 'happy-outline', color: '#F7B125' },
  WOW: { name: 'sad-outline', color: '#F7B125' },
  SAD: { name: 'sad', color: '#54C7EC' },
  ANGRY: { name: 'flame', color: '#E9710F' },
};

function formatCompactNumber(n: number): string {
  if (n < 1000) return n.toLocaleString();
  if (n < 1_000_000) return `${(n / 1000).toFixed(n % 1000 >= 100 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** Per-post "View insights" action — real Meta data for a single post, laid
 * out to match Meta's own Professional Dashboard grouping (Views card,
 * Interactions card with a reaction-by-type row, and video stats when
 * applicable). Same fixed-dark-canvas styling as FacebookInsightsModal.
 *
 * Deliberately does NOT replicate Meta's internal Professional Dashboard
 * sections that have no public Graph API equivalent — confirmed via Meta's
 * own insights reference docs: distinct "Viewers" count (vs. Views), Saves,
 * Net follows attributed to this post, traffic source, viewer age/gender/
 * country breakdown, and the "your typical content" benchmark comparison
 * line. Those are internal-only data DISLIX's API access cannot reach, so
 * showing them would mean fabricating numbers — omitted entirely rather
 * than faked. Views itself is also lifetime-only (no daily breakdown exists
 * for post_media_view), so it's shown as a single stat, not a chart. */
export const PostInsightsModal: React.FC<PostInsightsModalProps> = ({ visible, pageId, post, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PostInsightsData | null>(null);

  const load = useCallback(async () => {
    if (!post) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getFacebookPostInsights(pageId, post.postId, post.mediaType || 'none');
      if (res.statusCode === 1000 && res.data) {
        setData(res.data);
      } else {
        setError('Could not load insights for this post');
      }
    } catch (e: any) {
      setError(e?.message || 'Could not load insights for this post');
    } finally {
      setLoading(false);
    }
  }, [pageId, post]);

  useEffect(() => {
    if (visible && post) load();
  }, [visible, post, load]);

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <View style={styles.header}>
            <Text style={styles.headerTitle} numberOfLines={1}>Post insights</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={22} color="#E4E6EB" />
            </TouchableOpacity>
          </View>

          {loading ? (
            <View style={styles.loading}>
              <ActivityIndicator size="large" color="#4599FF" />
            </View>
          ) : error ? (
            <View style={styles.loading}>
              <Ionicons name="alert-circle-outline" size={24} color="#B0B3B8" />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : data ? (
            <ScrollView contentContainerStyle={styles.content}>
              {!!post?.message && (
                <Text style={styles.postPreview} numberOfLines={2}>{post.message}</Text>
              )}

              <View style={styles.card}>
                <View style={styles.cardTitleRow}>
                  <Text style={styles.cardTitle}>Views</Text>
                  <Ionicons name="information-circle-outline" size={13} color="#B0B3B8" />
                </View>
                <Text style={styles.bigStatValue}>{formatCompactNumber(data.views)}</Text>
                <Text style={styles.subStatRow}>Reach {formatCompactNumber(data.reach)}</Text>
              </View>

              <View style={styles.card}>
                <View style={styles.cardTitleRow}>
                  <Text style={styles.cardTitle}>{formatCompactNumber(data.reactionsTotal + data.comments + data.shares)} Interactions</Text>
                  <Ionicons name="information-circle-outline" size={13} color="#B0B3B8" />
                </View>
                <View style={styles.tilesRow}>
                  <View style={styles.tile}>
                    <View style={styles.tileTextWrap}>
                      <Text style={styles.tileValue}>{formatCompactNumber(data.reactionsTotal)}</Text>
                      <Text style={styles.tileLabel}>Reactions</Text>
                    </View>
                    <Ionicons name="thumbs-up-outline" size={18} color="#B0B3B8" />
                  </View>
                  <View style={styles.tile}>
                    <View style={styles.tileTextWrap}>
                      <Text style={styles.tileValue}>{formatCompactNumber(data.comments)}</Text>
                      <Text style={styles.tileLabel}>Comments</Text>
                    </View>
                    <Ionicons name="chatbubble-outline" size={18} color="#B0B3B8" />
                  </View>
                  <View style={styles.tile}>
                    <View style={styles.tileTextWrap}>
                      <Text style={styles.tileValue}>{formatCompactNumber(data.shares)}</Text>
                      <Text style={styles.tileLabel}>Shares</Text>
                    </View>
                    <Ionicons name="arrow-redo-outline" size={18} color="#B0B3B8" />
                  </View>
                  <View style={styles.tile}>
                    <View style={styles.tileTextWrap}>
                      <Text style={styles.tileValue}>{formatCompactNumber(data.linkClicks)}</Text>
                      <Text style={styles.tileLabel}>Link clicks</Text>
                    </View>
                    <Ionicons name="link-outline" size={18} color="#B0B3B8" />
                  </View>
                </View>

                <Text style={styles.reactionByTypeTitle}>Reaction by type</Text>
                <View style={styles.reactionIconsRow}>
                  {REACTION_TYPES_ORDERED.map((type) => {
                    const icon = REACTION_ICON[type];
                    const count = data.reactionsByType[type] ?? 0;
                    return (
                      <View key={type} style={styles.reactionIconColumn}>
                        <View style={[styles.reactionIconBadge, { backgroundColor: icon.color }]}>
                          <Ionicons name={icon.name} size={16} color="#FFFFFF" />
                        </View>
                        <Text style={styles.reactionIconCount}>{formatCompactNumber(count)}</Text>
                      </View>
                    );
                  })}
                </View>
              </View>

              {data.video && (
                <View style={styles.card}>
                  <Text style={styles.cardTitle}>Video performance</Text>
                  <View style={styles.tilesRow}>
                    <View style={styles.videoStatCard}>
                      <Text style={styles.tileValue}>{formatCompactNumber(data.video.threeSecondViews)}</Text>
                      <Text style={styles.tileLabel}>3-second views</Text>
                    </View>
                    <View style={styles.videoStatCard}>
                      <Text style={styles.tileValue}>{formatCompactNumber(data.video.views60s)}</Text>
                      <Text style={styles.tileLabel}>1-minute views</Text>
                    </View>
                    <View style={styles.videoStatCard}>
                      <Text style={styles.tileValue}>{formatCompactNumber(data.video.watchTimeMinutes)}m</Text>
                      <Text style={styles.tileLabel}>Watch time</Text>
                    </View>
                  </View>
                </View>
              )}
            </ScrollView>
          ) : null}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-end',
  },
  sheet: {
    maxHeight: '88%',
    backgroundColor: '#18191A',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#3A3B3C',
  },
  headerTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700' as const,
    color: '#E4E6EB',
    marginRight: 8,
  },
  loading: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 48,
  },
  errorText: {
    fontSize: 14,
    color: '#B0B3B8',
  },
  content: {
    padding: 16,
    gap: 12,
  },
  postPreview: {
    fontSize: 14,
    color: '#B0B3B8',
  },
  card: {
    backgroundColor: '#242526',
    borderRadius: 10,
    padding: 16,
    gap: 12,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: '700' as const,
    color: '#E4E6EB',
  },
  bigStatValue: {
    fontSize: 30,
    fontWeight: '700' as const,
    color: '#E4E6EB',
  },
  subStatRow: {
    fontSize: 13,
    color: '#B0B3B8',
  },
  tilesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  tile: {
    flexGrow: 1,
    flexBasis: '46%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#3A3B3C',
    borderRadius: 8,
    padding: 12,
  },
  tileTextWrap: {
    gap: 2,
  },
  tileValue: {
    fontSize: 18,
    fontWeight: '700' as const,
    color: '#E4E6EB',
  },
  tileLabel: {
    fontSize: 12,
    color: '#B0B3B8',
  },
  reactionByTypeTitle: {
    fontSize: 14,
    fontWeight: '700' as const,
    color: '#E4E6EB',
  },
  reactionIconsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  reactionIconColumn: {
    alignItems: 'center',
    gap: 6,
  },
  reactionIconBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reactionIconCount: {
    fontSize: 12,
    fontWeight: '600' as const,
    color: '#E4E6EB',
  },
  videoStatCard: {
    flexGrow: 1,
    flexBasis: '30%',
    backgroundColor: '#3A3B3C',
    borderRadius: 8,
    padding: 12,
    gap: 4,
  },
});
